<template lang="pug">
.mb-S(data-test-id="date-and-time-availability")
  label(v-if="label") {{ label }}
  briq-required-and-optional-markers(
    :isRequired="this.$attrs.required"
    :isDisabled="this.$attrs.disabled || this.$attrs.readonly"
    :hideOptionalMarker="hideOptionalMarker"
    :label="label"
    )
  briq-tooltip(:help="help")
  // todo, refactor with BriqTable.available-weekday-times-table
  table.table.table--striped.table--form.available-weekday-times-table
    thead
        th
          // todo: replace this by a refactored BriqCheckbox
          BriqCheckboxStandalone(
            v-model="areAllDaysSelected"
            styleType="grey-theme"
            class="checkbox-holder checkAllDays"
            data-test-id="check-all"
            )
        th Day
        th Start time
        th End time
        th
    tbody(:key="reRenderKey")
      tr(v-for="day in daysOfWeek" :data-test-id="`row-${day.toLowerCase()}`")
        td
          BriqCheckboxStandalone(
            :value="isCurrentDaySelected(day)"
            styleType="grey-theme"
            class="checkbox-holder"
            @input="v => selectDay({day, shouldSelect: v})"
            data-test-id="day-of-week-checkbox"
            )
        td 
          .day-name {{ day }}
        td.no-padding(data-test-id="start-time-cell")
          .time-component-holder(v-for="(time, timeKey) in getTimes(day)") 
            BriqTimeInput(
              :hide-clear-button="true"
              :value="time.StartTime"
              :twelve-hour-clock="$twelvehourclock"
              place-holder="--:--"
              emit-seconds
              @input="v => updateSelectedTime({type: 'StartTime', day, timePos: timeKey, value: v})"
              )
        td.no-padding(data-test-id="end-time-cell")
          .time-component-holder(v-for="(time, timeKey) in getTimes(day)") 
            BriqTimeInput(
              :hide-clear-button="true"
              :value="time.EndTime"
              place-holder="--:--"
              :twelve-hour-clock="$twelvehourclock"
              emit-seconds
              @input="v => updateSelectedTime({type: 'EndTime', day, timePos: timeKey, value: v})"
              )
        td.no-padding
          .action-buttons(v-for="(time, timeKey) in getTimes(day)")
            button.table__action.daytime-action.add-new-item-btn(
              type="button"
              @click="addNewTimeItem(day)"
              :class="{disabled: !isCurrentDaySelected(day)}"
              data-test-id="add-time-btn"
              )
              BriqIcon(name="icon-input-plus-icon")
            button.table__action.daytime-action.remove-item-btn(
              type="button"
              @click="removeTimeItem({day, timePos: timeKey})"
              :class="{disabled: !isCurrentDaySelected(day)}"
              data-test-id="remove-time-btn"
              )
              BriqIcon(name="icon-input-trash-icon")
  .validation-message(v-if="!isFormValid") Please make sure that all start times precede end times within 24 hours of the start of day time. Your start of day time is configured at {{ startOfDay }}.
</template>

<script>
import Vue from 'vue'
import { forEach, has, remove } from 'ramda'
import { getTimes, getNewTimes, allDaysAreSelected } from './dealAvailabilityFunctions.js'

export default {
  name: 'AvailableWeekdayTimes',
  inject: ['validations', 'startOfDay'],
  props: {
    label: String,
    help: String,
    hideOptionalMarker: { type: Boolean, default: false },
    deal: { type: Object, requred: true },
  },
  data() {
    return {
      daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      reRenderKey: 1,
    }
  },
  computed: {
    areAllDaysSelected: {
      get() {
        return allDaysAreSelected(this.deal.AvailableWeekdayTimes)
      },
      set(v) {
        v ? this.checkAllDays() : this.uncheckAllDays()
      },
    },
    isFormValid() {
      return !this.validations.deal.AvailableWeekdayTimes.$error
    },
  },
  methods: {
    getAvailableWeekdayTimes() {
      // Warning, do not make this into a computed because then Vue cannot detect when missing properties are added
      return this.deal.AvailableWeekdayTimes || {}
    },
    getTimes(day) {
      return getTimes(this.deal.AvailableWeekdayTimes)(day)
    },
    checkAllDays() {
      forEach(day => this.selectDay({ day, shouldSelect: true }))(this.daysOfWeek)
    },
    uncheckAllDays() {
      Vue.set(this.deal, 'AvailableWeekdayTimes', null)
      this.reRenderKey += 1 // force rerender
    },
    selectDay({ day, shouldSelect }) {
      const isSwitch = has(day, this.deal.AvailableWeekdayTimes) !== shouldSelect
      if (isSwitch) {
        const newTimes = getNewTimes(shouldSelect, day, this.deal.AvailableWeekdayTimes)
        Vue.set(this.deal, 'AvailableWeekdayTimes', newTimes)
        this.reRenderKey += 1 // force rerender
      }
    },
    isCurrentDaySelected(day) {
      if (!this.daysOfWeek.includes(day)) {
        this.$logError('isCurrentDaySelected called with day that is not in this.daysOfWeek', day)
        return undefined
      }
      return Array.isArray(this.getAvailableWeekdayTimes()[day])
    },
    updateSelectedTime({ type, day, timePos, value }) {
      if (this.deal.AvailableWeekdayTimes[day].length === 0) {
        this.deal.AvailableWeekdayTimes[day].push({ StartTime: value, EndTime: value })
      } else {
        Vue.set(this.deal.AvailableWeekdayTimes[day][timePos], type, value)
      }
      this.validations.$reset()
    },
    addNewTimeItem(day) {
      const newTime = { StartTime: '00:00:00', EndTime: '00:00:00' }
      this.getAvailableWeekdayTimes()[day].push(newTime)
    },
    removeTimeItem({ day, timePos }) {
      if (!this.deal.AvailableWeekdayTimes[day]) return
      this.deal.AvailableWeekdayTimes[day] = remove(
        timePos,
        1,
      )(this.deal.AvailableWeekdayTimes[day])
      this.reRenderKey += 1
    },
  },
}
</script>

<style scoped lang="stylus">
.available-weekday-times-table
  margin-top 30px

  td,
  th
    width 17.5%
    height 61px
    vertical-align top
    padding 15px 20px

    .day-name
      padding-top 10px

    .checkbox-holder
      margin 0 auto
      width 20px
      margin-top 10px

    .time-component-holder
      border-width 1px 0px 0px 0px
      border-color #ececec
      border-style solid
      padding 15px

    .action-buttons
      padding 13px 0px 13px 0px

      .disabled
        opacity 0.5
        cursor not-allowed

      .table__action + .table__action:before
        content ''
        display inline-block
        height 50px
        background #ececec
        width 1px
        position absolute
        left 0px
        top 52%
        transform translateY(-50%)

      .daytime-action
        padding 14px
        &:last-child
            padding-right 0

      .icon-input-plus-icon
        font-size 12px

  td:nth-child(1),
  th:nth-child(1)
    width 3%
    min-width 3%


  th:nth-child(2)
    width 63%
    min-width 62%

  th:nth-child(3),
  th:nth-child(4)
    width 12%
    min-width 12%

  th:nth-child(5)
    width 10%

.no-padding
  padding 0px !important
</style>
